import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { FiArrowLeft, FiPackage } from "react-icons/fi";
import { BsFiletypeXls } from "react-icons/bs";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { getActionTypeLabel } from "../../OtherData/actionTypeMap";
import { errorAlert } from "../../Components/Alerts";
import DateInputDdMmYyyy from "../../Components/DateInputDdMmYyyy";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { inventoryColumns as inventoryExportColumns, inventoryDetailsColumns, inventoryDetailsMappings, inventoryMappings } from "../../Components/ExcelConverter/AccountingReportsHeader";
import "./InventoryPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

function firstDefinedValue(source, keys, fallback = undefined) {
  if (!source || !Array.isArray(keys)) {
    return fallback;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }

  return fallback;
}

function normalizeInventoryItem(item) {
  const qtyInput = Number(firstDefinedValue(item, ["TotalQtyInput", "totalQtyInput", "qtyInput"], 0));
  const qtyOutput = Number(firstDefinedValue(item, ["TotalQtyOutput", "totalQtyOutput", "qtyOutput"], 0));
  const qtyInStockRaw = firstDefinedValue(item, ["TotalQtyInStock", "totalQtyInStock", "qtyStok", "qtyStock", "qtyInStock"]);
  const qtyInStock = qtyInStockRaw === undefined
    ? qtyInput - qtyOutput
    : Number(qtyInStockRaw);

  return {
    ...item,
    ArticleID: firstDefinedValue(item, ["ArticleID", "articleID", "articleId"], 0),
    ArticleName: firstDefinedValue(item, ["ArticleName", "articleName"], ""),
    Barcode: firstDefinedValue(item, ["Barcode", "barcode"], ""),
    ArticleGroupName: firstDefinedValue(item, ["ArticleGroupName", "articleGroupName", "groupName"], ""),
    ArticleUnitName: firstDefinedValue(item, ["ArticleUnitName", "articleUnitName", "unitName"], ""),
    TotalQtyInput: qtyInput,
    TotalQtyOutput: qtyOutput,
    TotalQtyInStock: qtyInStock,
    CmimiMesatar: Number(firstDefinedValue(item, ["CmimiMesatar", "cmimiMesatar", "averagePrice"], 0)),
    TotalAmount: Number(firstDefinedValue(item, ["TotalAmount", "totalAmount", "amount"], 0)),
  };
}

function normalizeInventoryDetailsItem(item) {
  return {
    ...item,
    HistoryID: firstDefinedValue(item, ["HistoryID", "historyID", "historyId"], 0),
    ActionType: firstDefinedValue(item, ["ActionType", "actionType"], ""),
    Barcode: firstDefinedValue(item, ["Barcode", "barcode"], ""),
    ArticleName: firstDefinedValue(item, ["ArticleName", "articleName"], ""),
    PartnerName: firstDefinedValue(item, ["PartnerName", "partnerName", "accountName"], ""),
    Date: firstDefinedValue(item, ["Date", "date"], ""),
    PriceWithoutVAT: Number(firstDefinedValue(item, ["PriceWithoutVAT", "priceWithoutVAT"], 0)),
    QTYInput: Number(firstDefinedValue(item, ["QTYInput", "QtyInput", "qtyInput"], 0)),
    QtyOutput: Number(firstDefinedValue(item, ["QtyOutput", "QTYOutput", "qtyOutput"], 0)),
    Progressive: Number(firstDefinedValue(item, ["Progressive", "progressive"], 0)),
  };
}

function formatDateForDisplay(date) {
  if (!date) {
    return "";
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const raw = String(date).trim();
  if (!raw) {
    return "";
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, year, month, day] = iso;
    return `${day}.${month}.${year}`;
  }

  const dmY = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmY) {
    const [, day, month, year] = dmY;
    return `${day}.${month}.${year}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}.${month}.${year}`;
}

export default function InventoryPage() {
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);
  const fiscalYearStart = `${fiscalYear}-01-01`;
  const fiscalYearEnd = `${fiscalYear}-12-31`;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totals, setTotals] = useState({
    totalQtyInput: 0,
    totalQtyOutput: 0,
    totalQtyInStock: 0,
    totalAmount: 0,
  });

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [companiesData, setCompaniesData] = useState([]);
  const [logoPath, setLogoPath] = useState("");

  const [showDetails, setShowDetails] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [articleDetails, setArticleDetails] = useState([]);
  const [articleDetailName, setArticleDetailName] = useState("");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsPaginationModel, setDetailsPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [detailsSortModel, setDetailsSortModel] = useState([]);
  const [detailsFilterModel, setDetailsFilterModel] = useState({ items: [] });
  const [detailsQuickFilter, setDetailsQuickFilter] = useState([]);
  const [detailsRowCount, setDetailsRowCount] = useState(0);
  const [detailsTotals, setDetailsTotals] = useState({ qtyInput: 0, qtyOutput: 0, progresive: 0 });

  useEffect(() => {
    setMinDate(fiscalYearStart);
    setMaxDate(fiscalYearEnd);
    setFromDate(fiscalYearStart);
    setToDate(fiscalYearEnd);
  }, [fiscalYear]);

  const hasCustomDateRange = useMemo(() => {
    if (!fromDate || !toDate) {
      return false;
    }

    return fromDate !== fiscalYearStart || toDate !== fiscalYearEnd;
  }, [fromDate, toDate, fiscalYearStart, fiscalYearEnd]);

  const fetchCompaniesData = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
      const companyItems = Array.isArray(response?.data) ? response.data : [];
      setCompaniesData(companyItems);
      setLogoPath(companyItems[0]?.CompanyLogo ? ApiLink.PHOTO_URL + companyItems[0].CompanyLogo : "");
    } catch (error) {
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave të kompanisë: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const buildParams = (page, pageSize, sort, filter, quick) => {
    const params = {
      fiscalYear,
      page,
      pageSize,
      sort: JSON.stringify(sort || []),
      filter: JSON.stringify(filter || []),
      quickFilter: (quick || []).join(" "),
    };

    if (hasCustomDateRange && fromDate && toDate) {
      params.fromDate = fromDate;
      params.toDate = toDate;
    }

    return params;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(ApiLink.API_URL + "InventoryReport/getAllInventoryData", {
        headers,
        params: buildParams(
          paginationModel.page,
          paginationModel.pageSize,
          sortModel,
          filterModel.items || [],
          quickFilter,
        ),
      });

      const payload = response?.data;
      const rawItems = Array.isArray(payload)
        ? payload
        : (payload?.items || payload?.data || []);
      const items = rawItems.map((item) => normalizeInventoryItem(item));
      const total = Array.isArray(payload)
        ? items.length
        : Number(payload?.total ?? payload?.totalRows ?? items.length);
      const payloadTotals = payload?.totals || {};

      const fallbackTotals = items.reduce((accumulator, item) => ({
        totalQtyInput: accumulator.totalQtyInput + Number(item.TotalQtyInput || 0),
        totalQtyOutput: accumulator.totalQtyOutput + Number(item.TotalQtyOutput || 0),
        totalQtyInStock: accumulator.totalQtyInStock + Number(item.TotalQtyInStock || 0),
        totalAmount: accumulator.totalAmount + Number(item.TotalAmount || 0),
      }), {
        totalQtyInput: 0,
        totalQtyOutput: 0,
        totalQtyInStock: 0,
        totalAmount: 0,
      });

      setData(items);
      setRowCount(total);
      setTotals({
        totalQtyInput: Number(payloadTotals.TotalQtyInput ?? payloadTotals.totalQtyInput ?? fallbackTotals.totalQtyInput),
        totalQtyOutput: Number(payloadTotals.TotalQtyOutput ?? payloadTotals.totalQtyOutput ?? fallbackTotals.totalQtyOutput),
        totalQtyInStock: Number(payloadTotals.TotalQtyInStock ?? payloadTotals.totalQtyInStock ?? fallbackTotals.totalQtyInStock),
        totalAmount: Number(payloadTotals.TotalAmount ?? payloadTotals.totalAmount ?? fallbackTotals.totalAmount),
      });
    } catch (error) {
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fiscalYear, paginationModel, sortModel, filterModel, quickFilter, fromDate, toDate]);

  useEffect(() => {
    fetchCompaniesData();
  }, [fiscalYear]);

  const handleViewDetails = async (rowPayload) => {
    const articleId = rowPayload?.ArticleID || rowPayload?.rowInfo?.ArticleID;
    if (!articleId) {
      return;
    }

    setSelectedArticleId(articleId);
    setArticleDetailName(rowPayload?.ArticleName || rowPayload?.rowInfo?.ArticleName || "");
    setDetailsPaginationModel({ page: 0, pageSize: 100 });
    setDetailsSortModel([]);
    setDetailsFilterModel({ items: [] });
    setDetailsQuickFilter([]);
    setShowDetails(true);
  };

  const fetchDetailsData = async () => {
    if (!showDetails || !selectedArticleId) {
      return;
    }

    setDetailsLoading(true);
    try {
      const response = await axios.get(ApiLink.API_URL + "InventoryReport/detailsPaged", {
        headers,
        params: {
          articleId: selectedArticleId,
          fiscalYear,
          page: detailsPaginationModel.page,
          pageSize: detailsPaginationModel.pageSize,
          sort: JSON.stringify(detailsSortModel),
          filter: JSON.stringify(detailsFilterModel.items || []),
          quickFilter: detailsQuickFilter.join(" "),
        },
      });

      const payload = response?.data;
      const rawItems = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
      const items = rawItems.map((item) => normalizeInventoryDetailsItem(item));
      const total = Array.isArray(payload)
        ? items.length
        : Number(payload?.total ?? payload?.totalRows ?? items.length);
      const totalsPayload = payload?.totals || {};

      const fallbackTotals = items.reduce((accumulator, item) => ({
        qtyInput: accumulator.qtyInput + Number(item.QTYInput || 0),
        qtyOutput: accumulator.qtyOutput + Number(item.QtyOutput || 0),
        progresive: Number(item.Progressive || accumulator.progresive),
      }), {
        qtyInput: 0,
        qtyOutput: 0,
        progresive: 0,
      });

      setArticleDetails(items);
      setDetailsRowCount(total);
      setDetailsTotals({
        qtyInput: Number(totalsPayload.totalQtyInput ?? totalsPayload.TotalQtyInput ?? fallbackTotals.qtyInput),
        qtyOutput: Number(totalsPayload.totalQtyOutput ?? totalsPayload.TotalQtyOutput ?? fallbackTotals.qtyOutput),
        progresive: Number(totalsPayload.lastProgressive ?? totalsPayload.LastProgressive ?? fallbackTotals.progresive),
      });
    } catch (error) {
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e detajeve: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë.");
      }
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetailsData();
  }, [
    showDetails,
    selectedArticleId,
    fiscalYear,
    detailsPaginationModel,
    detailsSortModel,
    detailsFilterModel,
    detailsQuickFilter,
  ]);

  const fetchAllInventoryRows = async () => {
    const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
    const response = await axios.get(ApiLink.API_URL + "InventoryReport/getAllInventoryData", {
      headers,
      params: buildParams(0, totalRowsToExport, sortModel, filterModel.items || [], quickFilter),
    });

    const payload = response?.data;
    const rows = Array.isArray(payload)
      ? payload
      : (payload?.items || payload?.data || []);

    return rows.map((item) => normalizeInventoryItem(item));
  };

  const mapInventoryExportData = (items) => (
    items.map((item) => ({
      ArticleName: item.ArticleName || "",
      Barcode: item.Barcode || "",
      ArticleGroupName: item.ArticleGroupName || "",
      ArticleUnitName: item.ArticleUnitName || "",
      TotalQtyInput: Number(item.TotalQtyInput || 0),
      TotalQtyOutput: Number(item.TotalQtyOutput || 0),
      TotalQtyInStock: Number(item.TotalQtyInStock || 0),
      CmimiMesatar: Number(item.CmimiMesatar || 0),
      TotalAmount: Number(item.TotalAmount || 0),
    }))
  );

  const mapInventoryDetailsExportData = (items) => (
    items.map((item) => ({
      ActionType: getActionTypeLabel(item.ActionType || ""),
      Barcode: item.Barcode || "",
      ArticleName: item.ArticleName || "",
      PartnerName: item.PartnerName || "",
      Date: formatDateForDisplay(item.Date),
      PriceWithoutVAT: Number(item.PriceWithoutVAT || 0),
      QTYInput: Number(item.QTYInput || 0),
      QtyOutput: Number(item.QtyOutput || 0),
      Progressive: Number(item.Progressive || 0),
    }))
  );

  const exportDatasetToExcel = (items, columns, mappings, fileName) => {
    if (!items || items.length === 0) {
      errorAlert("Nuk ka të dhëna për eksport.");
      return;
    }

    const filteredData = items.map((item) => (
      columns.reduce((accumulator, column) => {
        accumulator[mappings[column] || column] = item[column];
        return accumulator;
      }, {})
    ));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const excelFile = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(excelFile, `${fileName}.xlsx`);
  };

  const reportPeriodLabel = useMemo(() => {
    if (fromDate && toDate) {
      return `${formatDateForDisplay(fromDate)} - ${formatDateForDisplay(toDate)}`;
    }

    return `${fiscalYear}`;
  }, [fromDate, toDate, fiscalYear, hasCustomDateRange]);

  const sanitizeFileName = (value, fallback) => {
    const cleaned = String(value || "")
      .trim()
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");

    return cleaned || fallback;
  };

  const addLogo = (doc) => {
    if (!logoPath) {
      return;
    }

    doc.addImage(logoPath, "JPEG", 12, 10, 24, 24);
  };

  const addReportHeader = (doc, title, subtitle, periodLabel, rightMetaLabel = "") => {
    const company = companiesData[0];
    const pageWidth = doc.internal.pageSize.getWidth();

    addLogo(doc);

    doc.setDrawColor(40, 73, 69);
    doc.setLineWidth(0.5);
    doc.line(12, 38, pageWidth - 12, 38);

    doc.setTextColor(31, 58, 54);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(title, 40, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(95, 127, 115);
    doc.text(subtitle, 40, 24);

    doc.setFontSize(9);
    doc.setTextColor(31, 58, 54);
    doc.text(`Periudha: ${periodLabel}`, 12, 46);
    if (rightMetaLabel) {
      doc.setFont("helvetica", "bold");
      doc.text(rightMetaLabel, pageWidth - 12, 46, { align: "right" });
    }

    if (!company) {
      return 52;
    }

    const companyDetails = [
      company.CompanyName,
      `Numri Identifikues: ${company.IdentificationNumber || "-"}`,
      `Numri i TVSH-së: ${company.VATNumber || "-"}`,
      `Adresa: ${company.Address || "-"}`,
      `Tel: ${company.PhoneNumbers || "-"}`,
      `Email: ${company.Email || "-"}`,
    ];

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(31, 58, 54);
    doc.text(companyDetails[0], pageWidth - 12, 16, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(95, 127, 115);
    doc.text(companyDetails.slice(1), pageWidth - 12, 22, { align: "right" });

    return 52;
  };

  const addReportFooter = (doc, periodLabel, generatedOnLabel) => {
    const pageCount = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const brandingLabel = "© www.accountree.net";
    const footerCenterLabel = `Gjeneruar më: ${generatedOnLabel} | ${brandingLabel}`;

    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(212, 226, 219);
      doc.setLineWidth(0.3);
      doc.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(95, 127, 115);
      doc.text(`Periudha: ${periodLabel}`, 12, pageHeight - 5.5);
      doc.text(footerCenterLabel, pageWidth / 2, pageHeight - 5.5, { align: "center" });
      doc.text(`Faqe ${page} / ${pageCount}`, pageWidth - 12, pageHeight - 5.5, { align: "right" });
    }
  };

  const addPdfTable = (doc, headersRow, bodyRows, footRow, startY, baseColumnStyles) => {
    const horizontalMargin = 12;
    const availableTableWidth = doc.internal.pageSize.getWidth() - (horizontalMargin * 2);
    const baseTableWidth = Object.values(baseColumnStyles).reduce(
      (sum, style) => sum + Number(style.cellWidth || 0),
      0,
    );
    const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
    const columnStyles = Object.entries(baseColumnStyles).reduce((accumulator, [key, style]) => {
      accumulator[key] = {
        ...style,
        cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3)),
      };
      return accumulator;
    }, {});

    doc.autoTable({
      head: [headersRow],
      body: bodyRows,
      foot: [footRow],
      startY,
      showHead: "firstPage",
      showFoot: "lastPage",
      margin: {
        top: 16,
        left: horizontalMargin,
        right: horizontalMargin,
        bottom: 16,
      },
      theme: "grid",
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
        lineColor: [212, 226, 219],
        lineWidth: 0.1,
        textColor: [31, 58, 54],
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
      },
      bodyStyles: {
        valign: "middle",
      },
      footStyles: {
        fillColor: [231, 240, 235],
        textColor: [31, 58, 54],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [247, 250, 248],
      },
      didParseCell: (hookData) => {
        if (hookData.section !== "foot") {
          return;
        }

        const alignRightFromIndex = headersRow.length >= 10 ? 5 : 6;
        if (hookData.column.index >= alignRightFromIndex) {
          hookData.cell.styles.halign = "right";
        }
      },
      columnStyles,
    });
  };

  const buildInventoryPdfTableData = (items) => {
    let totalQtyInput = 0;
    let totalQtyOutput = 0;
    let totalQtyInStock = 0;
    let totalAmount = 0;

    const bodyRows = items.map((item, index) => {
      totalQtyInput += Number(item.TotalQtyInput || 0);
      totalQtyOutput += Number(item.TotalQtyOutput || 0);
      totalQtyInStock += Number(item.TotalQtyInStock || 0);
      totalAmount += Number(item.TotalAmount || 0);

      return [
        String(index + 1),
        item.ArticleName || "",
        item.Barcode || "",
        item.ArticleGroupName || "",
        item.ArticleUnitName || "",
        formatCurrency(item.TotalQtyInput),
        formatCurrency(item.TotalQtyOutput),
        formatCurrency(item.TotalQtyInStock),
        formatCurrency(item.CmimiMesatar),
        formatCurrency(item.TotalAmount),
      ];
    });

      const footRow = [
      "",
      "",
      "",
      "",
      "Totali",
      formatCurrency(totalQtyInput),
      formatCurrency(totalQtyOutput),
      formatCurrency(totalQtyInStock),
      "",
      formatCurrency(totalAmount),
    ];

    return { bodyRows, footRow };
  };

  const buildInventoryDetailsPdfTableData = (items) => {
    let totalQtyInput = 0;
    let totalQtyOutput = 0;
    let lastProgressive = 0;

    const bodyRows = items.map((item, index) => {
      totalQtyInput += Number(item.QTYInput || 0);
      totalQtyOutput += Number(item.QtyOutput || 0);
      lastProgressive = Number(item.Progressive || 0);

      return [
        String(index + 1),
        getActionTypeLabel(item.ActionType || ""),
        item.Barcode || "",
        item.ArticleName || "",
        item.PartnerName || "",
        formatDateForDisplay(item.Date),
        formatCurrency(item.PriceWithoutVAT),
        formatCurrency(item.QTYInput),
        formatCurrency(item.QtyOutput),
        formatCurrency(item.Progressive),
      ];
    });

    const footRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "Totali",
      formatCurrency(totalQtyInput),
      formatCurrency(totalQtyOutput),
      formatCurrency(lastProgressive),
    ];

    return { bodyRows, footRow };
  };

  const exportInventoryPdf = async () => {
    const generatedDocument = await buildInventoryPdfDocument();
    if (!generatedDocument) {
      return;
    }

    const { doc, companyNameForFile } = generatedDocument;
    doc.save(`Stoku_${formatDateForDisplay(fromDate)}_deri_${formatDateForDisplay(toDate)}_${companyNameForFile}.pdf`);
  };

  const buildInventoryPdfDocument = async () => {
    const items = await fetchAllInventoryRows();
    if (!items.length) {
      errorAlert("Nuk ka të dhëna për eksport.");
      return null;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const generatedOnLabel = formatDateForDisplay(new Date());
    const startY = addReportHeader(doc, "Stoku", "Raport financiar i stokut", reportPeriodLabel);
    const { bodyRows, footRow } = buildInventoryPdfTableData(items);

    addPdfTable(
      doc,
      ["#", "Artikulli", "Barkodi", "Grupi Artikullit", "Njësia", "Sasia Hyrje", "Sasia Dalje", "Sasia në Stok", "Çmimi Mesatar", "Vlera Totale"],
      bodyRows,
      footRow,
      startY,
      {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 38 },
        2: { cellWidth: 22 },
        3: { cellWidth: 26 },
        4: { cellWidth: 18 },
        5: { cellWidth: 20, halign: "right" },
        6: { cellWidth: 20, halign: "right" },
        7: { cellWidth: 20, halign: "right" },
        8: { cellWidth: 18, halign: "right" },
        9: { cellWidth: 22, halign: "right" },
      },
    );

    addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

    return {
      doc,
      companyNameForFile: sanitizeFileName(companiesData?.[0]?.CompanyName, "Kompania"),
    };
  };

  const exportInventoryDetailsPdf = async () => {
    const generatedDocument = await buildInventoryDetailsPdfDocument();
    if (!generatedDocument) {
      return;
    }

    const { doc, articleNameForFile } = generatedDocument;
    doc.save(`${articleNameForFile}.pdf`);
  };

  const fetchAllInventoryDetailRows = async () => {
    if (!selectedArticleId) {
      return [];
    }

    const totalRowsToExport = Math.max(Number(detailsRowCount) || 0, 1);
    const response = await axios.get(ApiLink.API_URL + "InventoryReport/detailsPaged", {
      headers,
      params: {
        articleId: selectedArticleId,
        fiscalYear,
        page: 0,
        pageSize: totalRowsToExport,
        sort: JSON.stringify(detailsSortModel),
        filter: JSON.stringify(detailsFilterModel.items || []),
        quickFilter: detailsQuickFilter.join(" "),
      },
    });

    const payload = response?.data;
    const rows = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
    return rows.map((item) => normalizeInventoryDetailsItem(item));
  };

  const buildInventoryDetailsPdfDocument = async () => {
    const detailsItems = await fetchAllInventoryDetailRows();
    if (!detailsItems.length) {
      errorAlert("Nuk ka të dhëna për eksport.");
      return null;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const generatedOnLabel = formatDateForDisplay(new Date());
    const detailPeriodLabel = reportPeriodLabel;
    const articleHeaderLabel = articleDetailName ? `Artikulli: ${articleDetailName}` : "";
    const startY = addReportHeader(
      doc,
      "Kartela Artikullit",
      "Raport i lëvizjeve të artikullit",
      detailPeriodLabel,
      articleHeaderLabel,
    );
    const { bodyRows, footRow } = buildInventoryDetailsPdfTableData(detailsItems);

    addPdfTable(
      doc,
      ["#", "Tipi Veprimit", "Barkodi", "Emri Artikullit", "Emri i Partnerit", "Data", "Çmimi pa TVSH", "Sasia Hyrje", "Sasia Dalje", "Progresive"],
      bodyRows,
      footRow,
      startY,
      {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 22 },
        2: { cellWidth: 18 },
        3: { cellWidth: 34 },
        4: { cellWidth: 32 },
        5: { cellWidth: 18, halign: "center" },
        6: { cellWidth: 18, halign: "right" },
        7: { cellWidth: 16, halign: "right" },
        8: { cellWidth: 16, halign: "right" },
        9: { cellWidth: 16, halign: "right" },
      },
    );

    addReportFooter(doc, detailPeriodLabel, generatedOnLabel);

    return {
      doc,
      articleNameForFile: sanitizeFileName(articleDetailName, "ArticleDetailsInventory"),
    };
  };

  const printPdfDocument = (doc) => {
    const pdfBlob = doc.output("blob");
    const printUrl = URL.createObjectURL(pdfBlob);
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.left = "-10000px";
    printFrame.style.top = "0";
    printFrame.style.width = "1024px";
    printFrame.style.height = "768px";
    printFrame.style.border = "0";
    printFrame.style.opacity = "0";
    printFrame.style.pointerEvents = "none";
    let didPrint = false;
    let cleanupDone = false;

    const cleanup = () => {
      if (cleanupDone) {
        return;
      }

      cleanupDone = true;
      URL.revokeObjectURL(printUrl);
      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    };

    printFrame.onload = () => {
      if (didPrint) {
        return;
      }

      didPrint = true;

      try {
        const frameWindow = printFrame.contentWindow;
        if (!frameWindow) {
          cleanup();
          errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
          return;
        }

        window.setTimeout(() => {
          frameWindow.focus();
          frameWindow.onafterprint = cleanup;
          frameWindow.print();
          window.setTimeout(cleanup, 5000);
        }, 450);
      } catch (error) {
        cleanup();
        errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
      }
    };

    document.body.appendChild(printFrame);
    printFrame.src = printUrl;

    window.setTimeout(() => {
      cleanup();
    }, 60_000);
  };

  const handlePrintInventoryPdf = async () => {
    const generatedDocument = await buildInventoryPdfDocument();
    if (!generatedDocument) {
      return;
    }

    printPdfDocument(generatedDocument.doc);
  };

  const handlePrintInventoryDetailsPdf = async () => {
    const generatedDocument = await buildInventoryDetailsPdfDocument();
    if (!generatedDocument) {
      return;
    }

    printPdfDocument(generatedDocument.doc);
  };

  const handleExportMainXlsx = async () => {
    const items = await fetchAllInventoryRows();
    exportDatasetToExcel(
      mapInventoryExportData(items),
      inventoryExportColumns,
      inventoryMappings,
      `Stoku_${formatDateForDisplay(fromDate)}_deri_${formatDateForDisplay(toDate)}`,
    );
  };

  const handleExportDetailsXlsx = async () => {
    const detailsItems = await fetchAllInventoryDetailRows();
    exportDatasetToExcel(
      mapInventoryDetailsExportData(detailsItems),
      inventoryDetailsColumns,
      inventoryDetailsMappings,
      `${articleDetailName || "StokuDetaje"}_StokuDetaje`,
    );
  };

  const inventoryColumns = useMemo(() => ([
    { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
    { field: "accName", headerName: "Emri Artikullit", minWidth: 220, width: 250 },
    { field: "barcode", headerName: "Barkodi", minWidth: 130, width: 150 },
    { field: "group", headerName: "Grupi", minWidth: 160, width: 180 },
    { field: "unit", headerName: "Njësia Matëse", minWidth: 140, width: 160 },
    { field: "qtyInput", headerName: "Sasia Total Hyrje", minWidth: 160, width: 190, valueFormatter: (value) => formatCurrency(value) },
    { field: "qtyOutput", headerName: "Sasia Total Dalje", minWidth: 160, width: 190, valueFormatter: (value) => formatCurrency(value) },
    { field: "qtyStok", headerName: "Sasia Total në Stok", minWidth: 170, width: 200, valueFormatter: (value) => formatCurrency(value) },
    { field: "price", headerName: "Çmimi Mesatar", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    { field: "total", headerName: "Vlera Totale", minWidth: 150, width: 170, valueFormatter: (value) => formatCurrency(value) },
  ]), []);

  const rows = useMemo(() => (
    data.map((item, index) => ({
      id: item.ArticleID || `inventory-${index}`,
      rowNumber: paginationModel.page * paginationModel.pageSize + index + 1,
      accName: item.ArticleName || "",
      barcode: item.Barcode || "",
      group: item.ArticleGroupName || "",
      unit: item.ArticleUnitName || "",
      qtyInput: item.TotalQtyInput || 0,
      qtyOutput: item.TotalQtyOutput || 0,
      qtyStok: item.TotalQtyInStock || 0,
      price: item.CmimiMesatar || 0,
      total: item.TotalAmount || 0,
      ArticleID: item.ArticleID,
      rowInfo: item,
    }))
  ), [data, paginationModel.page, paginationModel.pageSize]);

  const summaryFooter = useMemo(() => ({
    labelField: "unit",
    label: "Totali",
    values: {
      qtyInput: formatCurrency(totals.totalQtyInput),
      qtyOutput: formatCurrency(totals.totalQtyOutput),
      qtyStok: formatCurrency(totals.totalQtyInStock),
      total: formatCurrency(totals.totalAmount),
    },
  }), [totals]);

  const detailsColumns = useMemo(() => ([
    { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
    { field: "type", headerName: "Tipi Veprimit", minWidth: 130, width: 150 },
    { field: "barcode", headerName: "Barkodi", minWidth: 130, width: 150 },
    { field: "articleName", headerName: "Emri Artikullit", minWidth: 220, width: 250 },
    { field: "accName", headerName: "Emri i Partnerit", minWidth: 220, width: 250 },
    { field: "date", headerName: "Data", minWidth: 120, width: 140 },
    { field: "priceNoVAT", headerName: "Çmimi pa TVSH", minWidth: 130, width: 160, valueFormatter: (value) => formatCurrency(value) },
    { field: "qtyInput", headerName: "Sasia Hyrje", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    { field: "qtyOutput", headerName: "Sasia Dalje", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    { field: "progresive", headerName: "Progresive", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
  ]), []);

  const detailsRows = useMemo(() => (
    articleDetails.map((item, index) => ({
      id: item.HistoryID || `inventory-detail-${index}`,
      rowNumber: index + 1,
      type: getActionTypeLabel(item.ActionType || ""),
      barcode: item.Barcode || "",
      articleName: item.ArticleName || "",
      accName: item.PartnerName || "",
      date: formatDateForDisplay(item.Date),
      priceNoVAT: item.PriceWithoutVAT || 0,
      qtyInput: item.QTYInput || 0,
      qtyOutput: item.QtyOutput || 0,
      progresive: item.Progressive || 0,
      rowInfo: item,
    }))
  ), [articleDetails]);

  const detailsSummaryFooter = useMemo(() => ({
    labelField: "priceNoVAT",
    label: "Totali",
    values: {
      qtyInput: formatCurrency(detailsTotals.qtyInput),
      qtyOutput: formatCurrency(detailsTotals.qtyOutput),
      progresive: formatCurrency(detailsTotals.progresive),
    },
  }), [detailsTotals]);

  return (
    <div className="Crud__Container INVP__page">
      <div className="INVP__content">
        {!showDetails && (
          <>
            <CrudPageHeader
              icon={FiPackage}
              title="Stoku"
              description="Kjo formë e stokut është e përshtatur për kontroll, filtrim dhe analizë profesionale të artikujve."
              className="INVP__header"
            />

            <div className="INVP__reportGrid">
              <ReusableAccountBooksGrid
                columns={inventoryColumns}
                rows={rows}
                loading={loading}
                rowCount={rowCount}
                height={650}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                sortModel={sortModel}
                setSortModel={setSortModel}
                filterModel={filterModel}
                setFilterModel={setFilterModel}
                quickFilter={quickFilter}
                setQuickFilter={setQuickFilter}
                showViewAction={true}
                onView={handleViewDetails}
                showEditAction={false}
                showDeleteAction={false}
                showExportButton={false}
                summaryFooter={summaryFooter}
                toolbarActions={(
                  <>
                    <AddActionsButton
                      text="Print"
                      onClick={handlePrintInventoryPdf}
                      icon={<AiOutlinePrinter />}
                      className="RSDG__toolButton INVP__gridActionBtn--print"
                    />
                    <AddActionsButton
                      text="PDF"
                      onClick={exportInventoryPdf}
                      icon={<AiOutlineFilePdf />}
                      className="RSDG__toolButton INVP__gridActionBtn--pdf"
                    />
                    <AddActionsButton
                      text="XLSX"
                      onClick={handleExportMainXlsx}
                      icon={<BsFiletypeXls />}
                      className="RSDG__toolButton INVP__gridActionBtn--xlsx"
                    />
                  </>
                )}
                toolbarRightActions={(
                  <div className="INVP__toolbarRightGroup">
                    <div className="INVP__dateFilters">
                      <div className="INVP__toolbarDateWrap INVP__toolbarDateWrap--date">
                        <DateInputDdMmYyyy
                          label="Nga"
                          value={fromDate}
                          minDate={minDate}
                          maxDate={maxDate}
                          onChange={setFromDate}
                          fullWidth={false}
                        />
                      </div>
                      <div className="INVP__toolbarDateWrap INVP__toolbarDateWrap--date">
                        <DateInputDdMmYyyy
                          label="Deri"
                          value={toDate}
                          minDate={minDate}
                          maxDate={maxDate}
                          onChange={setToDate}
                          fullWidth={false}
                        />
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          </>
        )}

        {showDetails && (
          <>
            <CrudPageHeader
              icon={FiPackage}
              title={`Kartela Artikullit${articleDetailName ? ` - ${articleDetailName}` : ""}`}
              description="Shfaqen të gjitha lëvizjet për artikullin e zgjedhur."
              className="INVP__header"
              action={(
                <AddActionsButton
                  text="Kthehu te lista"
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedArticleId(null);
                    setArticleDetails([]);
                    setDetailsRowCount(0);
                    setDetailsTotals({ qtyInput: 0, qtyOutput: 0, progresive: 0 });
                    setDetailsPaginationModel({ page: 0, pageSize: 100 });
                    setDetailsSortModel([]);
                    setDetailsFilterModel({ items: [] });
                    setArticleDetailName("");
                    setDetailsQuickFilter([]);
                  }}
                  icon={<FiArrowLeft />}
                  className="INVP__backBtn"
                />
              )}
            />

            <div className="INVP__reportGrid INVP__reportGrid--details">
              <ReusableAccountBooksGrid
                columns={detailsColumns}
                rows={detailsRows}
                loading={detailsLoading}
                rowCount={detailsRowCount}
                height={650}
                paginationModel={detailsPaginationModel}
                setPaginationModel={setDetailsPaginationModel}
                sortModel={detailsSortModel}
                setSortModel={setDetailsSortModel}
                filterModel={detailsFilterModel}
                setFilterModel={setDetailsFilterModel}
                quickFilter={detailsQuickFilter}
                setQuickFilter={setDetailsQuickFilter}
                showViewAction={false}
                showEditAction={false}
                showDeleteAction={false}
                showExportButton={false}
                summaryFooter={detailsSummaryFooter}
                toolbarActions={(
                  <>
                    <AddActionsButton
                      text="Print"
                      onClick={handlePrintInventoryDetailsPdf}
                      icon={<AiOutlinePrinter />}
                      className="RSDG__toolButton INVP__gridActionBtn--print"
                    />
                    <AddActionsButton
                      text="PDF"
                      onClick={exportInventoryDetailsPdf}
                      icon={<AiOutlineFilePdf />}
                      className="RSDG__toolButton INVP__gridActionBtn--pdf"
                    />
                    <AddActionsButton
                      text="XLSX"
                      onClick={handleExportDetailsXlsx}
                      icon={<BsFiletypeXls />}
                      className="RSDG__toolButton INVP__gridActionBtn--xlsx"
                    />
                  </>
                )}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}